import { useEffect, useRef } from 'react';

interface AutoCalculateOptions {
  tickers: string[];
  startDate: string;
  endDate: string;
  initialInvestment: number;
  adjustInflation: boolean;
  enabled?: boolean;
  debounceMs?: number;
}

export function useAutoCalculate(
  options: AutoCalculateOptions,
  onCalculate: () => void
) {
  const {
    tickers,
    startDate,
    endDate,
    initialInvestment,
    adjustInflation,
    enabled = true,
    debounceMs = 500,
  } = options;

  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const prevParamsRef = useRef<string>('');
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevParamsRef.current = JSON.stringify({ startDate, endDate, initialInvestment, adjustInflation });
      return;
    }

    if (!enabled || tickers.length === 0) return;

    const currentParams = JSON.stringify({ startDate, endDate, initialInvestment, adjustInflation });

    // Only recalculate if params changed (not just tickers)
    if (prevParamsRef.current !== currentParams) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onCalculate();
      }, debounceMs);
      prevParamsRef.current = currentParams;
    }

    return () => clearTimeout(debounceTimerRef.current);
  }, [startDate, endDate, initialInvestment, adjustInflation, tickers.length, enabled, debounceMs, onCalculate]);
}
